import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";
import { track, events } from "../lib/analytics";
import { resetConsent } from "../lib/cookieConsent";
import { STATES } from "../lib/states";
import { CITIES } from "../lib/cities";
import { COMPARISONS } from "../lib/comparisons";
import { GLOSSARY } from "../lib/glossary";
import { useCompareOverrides, useGlossaryOverrides, useTrustStripOverrides } from "../lib/footerData";
import {
  ArrowRight,
  Mail,
  ChevronDown,
  Info,
  ShieldCheck,
  Award,
  Eye,
  FileCheck,
  User,
  Building2,
} from "lucide-react";
import GeoFooterCard from "./GeoFooterCard";

// SEO mega-footer curation
const SORTED_STATES = [...STATES].sort((a, b) => a.name.localeCompare(b.name));

// ─── Iter-190.E — Brand-accurate social icons ──────────────────────────────
// Inlined as monochrome SVG paths (currentColor) so they inherit the
// footer's text color and `hover:text-[#10B981]` accent. Lucide-react
// doesn't ship X / Pinterest / TikTok / YouTube glyphs, so we ship all
// six inline for visual consistency. Each entry: { name, label, href,
// path }. The `path` is a 24×24 viewBox brand glyph (Simple Icons family).
const SOCIAL_LINKS = [
  {
    name: "instagram",
    label: "Follow CarSavr on Instagram",
    href: "https://www.instagram.com/carsavr",
    path: (
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.336 3.608 1.311.975.975 1.249 2.242 1.311 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.336 2.633-1.311 3.608-.975.975-2.242 1.249-3.608 1.311-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.336-3.608-1.311-.975-.975-1.249-2.242-1.311-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.336-2.633 1.311-3.608.975-.975 2.242-1.249 3.608-1.311 1.266-.058 1.646-.07 4.85-.07zm0 5.838a4 4 0 100 8 4 4 0 000-8zm0 6.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5zm5.25-6.75a1 1 0 11-2 0 1 1 0 012 0z" />
    ),
  },
  {
    name: "facebook",
    label: "Like CarSavr on Facebook",
    href: "https://www.facebook.com/share/1bU2cwW4PL/",
    path: (
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879v-6.988h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.891h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
    ),
  },
  {
    name: "x",
    label: "Follow CarSavr on X",
    href: "https://x.com/carsavr",
    path: (
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    ),
  },
  {
    name: "pinterest",
    label: "Save CarSavr ideas on Pinterest",
    href: "https://pin.it/7KAagrIdQ",
    path: (
      <path d="M12 0a12 12 0 00-4.373 23.179c-.105-.928-.2-2.354.041-3.366.219-.91 1.41-5.793 1.41-5.793s-.36-.72-.36-1.78c0-1.665.967-2.91 2.17-2.91 1.02 0 1.515.766 1.515 1.685 0 1.025-.654 2.56-.99 3.98-.282 1.19.595 2.16 1.766 2.16 2.122 0 3.755-2.235 3.755-5.466 0-2.86-2.054-4.86-4.99-4.86-3.398 0-5.392 2.55-5.392 5.18 0 1.026.395 2.126.888 2.724.097.118.111.222.083.343-.09.378-.293 1.19-.333 1.358-.052.22-.174.267-.4.16-1.5-.697-2.437-2.89-2.437-4.654 0-3.785 2.75-7.262 7.93-7.262 4.163 0 7.398 2.967 7.398 6.935 0 4.137-2.609 7.464-6.227 7.464-1.216 0-2.36-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24a12 12 0 100-24" />
    ),
  },
  {
    name: "youtube",
    label: "Subscribe to CarSavr on YouTube",
    href: "https://youtube.com/@carsavr",
    path: (
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.546 15.568V8.432L15.818 12l-6.272 3.568z" />
    ),
  },
  {
    name: "tiktok",
    label: "Follow CarSavr on TikTok",
    href: "https://www.tiktok.com/@carsavr.com",
    path: (
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.84-.1z" />
    ),
  },
];

function SocialIcon({ children, className = "w-4 h-4" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      {children}
    </svg>
  );
}

const POPULAR_CITY_SLUGS = [
  "new-york-city", "los-angeles", "chicago", "houston", "phoenix",
  "philadelphia", "san-antonio", "san-diego", "dallas", "austin",
  "miami", "atlanta", "boston", "seattle", "denver",
];
const POPULAR_CITIES = POPULAR_CITY_SLUGS
  .map((slug) => CITIES.find((c) => c.slug === slug))
  .filter(Boolean);

const POPULAR_GLOSSARY_SLUGS = [
  "apr", "refinance", "gap-insurance", "deductible", "pip",
  "fico-score", "ltv", "dti", "msrp", "extended-warranty",
];
const POPULAR_GLOSSARY = POPULAR_GLOSSARY_SLUGS
  .map((slug) => GLOSSARY.find((g) => g.slug === slug))
  .filter(Boolean);

// Iter-278.I — Footer Newsletter Simplification (founder audit 2026-02).
// The previous "FOOTER_CTA_CHIPS" row (Compare insurance / Compare loans /
// Run a calculator) added visual noise + competing CTAs immediately
// adjacent to the newsletter input. Newsletter signup is the highest-
// conversion footer surface; chips were stealing focus and adding ~60px
// of height with no measurable click-through. Removed per audit; the
// four primary verticals are still one click away via the Save Money
// column to the right. See /app/FOOTER_NEWSLETTER_AUDIT.md.

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [seoOpen, setSeoOpen] = useState(false);
  // Iter-189.E Sprint 5 — Compare & Learn parent collapsible. Default
  // closed on desktop too (the user wants the whole footer to read
  // cleanly above the legal row; SEO content stays in the DOM via
  // <details>, so crawlers still index it).
  const [compareOpen, setCompareOpen] = useState(false);

  // Iter-189.E Sprint 5 — Admin overrides for the 3 editable footer
  // sections. Each hook returns `null` (or null-field defaults) when
  // no override exists, signalling "fall back to hardcoded literal".
  const compareOverride = useCompareOverrides();
  const glossaryOverride = useGlossaryOverrides();
  const trustStrip = useTrustStripOverrides();

  // Iter-196.X (F-MON-04 from audit) — Newsletter vertical-interest
  // segmentation. Captures which verticals each subscriber cares
  // about so future Resend audience routing can segment drips by
  // Iter-196.LL Sprint 4 (Privacy M-5) — Interest checkboxes default to
  // ALL-UNCHECKED. The previous "default-checked" pattern was a soft
  // negative-option flag (PIRG / EFF). Visitors now affirmatively
  // choose which verticals they want emails about; submit is gated
  // until at least one is selected.
  const [interests, setInterests] = useState(() => ({
    insurance: false,
    loans: false,
    refinance: false,
    warranty: false,
  }));
  const toggleInterest = (key) =>
    setInterests((prev) => ({ ...prev, [key]: !prev[key] }));

  // Iter-196.WW (L-6) — Honeypot anti-spam field state. Real users
  // never see or touch this input (off-screen + aria-hidden + tabIndex
  // -1); bot frameworks fill every field they find, which sends the
  // signup to the backend dead-letter collection.
  const [honeypot, setHoneypot] = useState("");

  // Resolved render lists: override wins; otherwise canonical defaults.
  // Both override types are `[{slug, label/term}]` so we map them to
  // the existing render shape (`{slug, title}` for compare, `{slug, term}`
  // for glossary) so the downstream JSX doesn't change.
  const compareList = compareOverride
    ? compareOverride.map((it) => ({ slug: it.slug, title: it.label }))
    : COMPARISONS;
  const glossaryList = glossaryOverride
    ? glossaryOverride.map((it) => ({ slug: it.slug, term: it.term }))
    : POPULAR_GLOSSARY;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    // Iter-196.LL Sprint 4 (Privacy M-5) — Require at least one
    // explicitly-selected vertical before allowing submit. Replaces
    // the previous "all-default-checked" negative-option pattern.
    const selectedCount = Object.values(interests).filter(Boolean).length;
    if (selectedCount === 0) {
      return toast.error("Pick at least one topic to subscribe to.");
    }
    setLoading(true);
    // Iter-196.X (F-MON-04) — Compose the interests array from the
    // 4 vertical checkboxes. Defaults to all-four-checked so the
    // payload is always non-empty; the backend further filters
    // against the canonical 4-vertical vocabulary.
    const interestArr = Object.entries(interests)
      .filter(([, v]) => v)
      .map(([k]) => k);
    track(events.NEWSLETTER_SUBMIT, {
      location: "footer",
      interests: interestArr.join(","),
      interest_count: interestArr.length,
    });
    try {
      await subscribeNewsletter(email, { interests: interestArr, website: honeypot });
      track(events.NEWSLETTER_SUCCESS, {
        location: "footer",
        interest_count: interestArr.length,
      });
      // Iter-196.X (F-UX-11 from audit) — Success toast now sets
      // cadence expectations explicitly so the welcome moment
      // doubles as an LTV signal. "1 email/month, unsub one click"
      // is a defensible, non-spam cadence.
      toast.success(
        "You're in. Expect weekly tips — unsubscribe anytime with one click.",
      );
      setEmail("");
    } catch {
      // Iter-196.X (F-UX-12) — Helpful failure copy.
      toast.error("Email service hiccup. Try once more — usually clears in 30 sec.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A2540] text-slate-100 mt-24"
    >
      {/* Iter-51: Trust strip above the main footer — visible signals lift
          E-E-A-T / affiliate-approval readiness without adding visual noise.
          Three concrete claims, no marketing copy.
          Iter-189.E Sprint 5 — Each badge's title + microcopy is now
          admin-editable via /admin?tab=footer-cms. `|| "..."` falls
          back to the hardcoded literal when no override exists.
          Iter-190.A4 (user request) — Icons + alignment refined:
            - FTC-compliant → FileCheck (was ShieldCheck, which felt
              too insurance-shield in the insurance context)
            - Independently owned → User (was Eye, single-person now
              communicates ownership more directly)
            - Three badges now line up flush at `items-start` with a
              consistent icon-top offset (`pt-0.5`) so the labels sit
              on the same baseline across columns regardless of how
              their microcopy wraps. */}
      <div
        data-testid="footer-trust-strip"
        className="border-t border-b border-white/10 bg-slate-950/40"
      >
        <div className="max-w-wide mx-auto px-6 lg:px-10 py-5">
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-xs text-slate-300">
            <li className="flex items-start gap-2.5">
              <FileCheck className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
              <span className="leading-snug">
                <span className="font-bold text-white">{trustStrip.badge_1_title || "FTC-compliant disclosures"}</span>
                <span className="text-slate-500"> · {trustStrip.badge_1_copy || "Every affiliate offer flagged"}</span>
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <Award className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
              <span className="leading-snug">
                <span className="font-bold text-white">{trustStrip.badge_2_title || "Editor-reviewed content"}</span>
                <span className="text-slate-500"> · {trustStrip.badge_2_copy || "Reviewed quarterly"}</span>
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <User className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
              <span className="leading-snug">
                <span className="font-bold text-white">{trustStrip.badge_3_title || "Independently owned"}</span>
                <span className="text-slate-500"> · {trustStrip.badge_3_copy || "No carrier ownership"}</span>
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-wide mx-auto px-6 lg:px-10 py-10 lg:py-12">
        {/* Newsletter row — Iter-278.I simplification: reduced vertical
            padding (py-16→py-10), tightened type rhythm, removed the
            redundant CTA chip row, slimmed the interest-pill UI. Goal
            was to cut ~35% of the section height without sacrificing the
            privacy-required opt-in interest selectors. */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-8 border-b border-white/10">
          <div className="lg:col-span-6">
            {/* Iter-189.G(b) — Inline wordmark restored (matches the
                page-loaded Cabinet Grotesk webfont). SVG brand asset
                is downloadable via the "Brand kit" link in the legal row. */}
            <div className="mb-3">
              <span
                data-testid="footer-wordmark"
                className="font-display font-black text-3xl text-white tracking-tight leading-none"
              >
                CarSav<span className="text-[#10B981]">r</span>
              </span>
            </div>
            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter leading-[1.05] text-white mb-2 max-w-md">
              Save money on every mile.
            </h2>
            <p className="text-slate-500 leading-snug max-w-md mb-2 text-sm">
              {/* Iter-278.I — Concise, benefit-led body copy. Replaces
                  the previous "Get occasional savings tips and ownership
                  insights." subhead with a more specific value
                  proposition that lists exactly what subscribers get. */}
              Get practical tips to lower your insurance, loan payments, and
              total cost of owning a car—plus new calculators, rate alerts,
              and buying guides.
            </p>
            <p className="text-slate-500 mb-4 text-xs">
              Weekly. Free. No spam. Unsubscribe anytime.
            </p>

            {/* Iter-196.LL Sprint 4 (Privacy M-5) / Iter-278.I — Vertical-
                interest pills. Default all-UNCHECKED so visitors must
                affirmatively pick at least one. Iter-278.I slimmed the
                row (compacted padding + tighter eyebrow) so the privacy-
                required selector remains but the visual weight drops. */}
            <div
              data-testid="newsletter-interests-row"
              role="group"
              aria-label="Pick which CarSavr verticals to receive emails about"
              className="max-w-md mb-2.5"
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 block mb-1.5">
                I want emails about <span className="text-[#10B981]">(pick at least one)</span>
              </span>
              {/* Iter-278.I follow-on — 2×2 grid on narrow viewports
                  (mobile previously wrapped pills onto 2-3 ragged rows
                  because of pill-width variance). At ≥sm we revert to
                  free-flow so the four pills sit on a single line. */}
              <div className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap">
              {[
                { key: "insurance", label: "Insurance" },
                { key: "loans", label: "Loans" },
                { key: "refinance", label: "Refinance" },
                { key: "warranty", label: "Warranty" },
              ].map((v) => {
                const checked = !!interests[v.key];
                return (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => toggleInterest(v.key)}
                    role="checkbox"
                    aria-checked={checked}
                    data-testid={`newsletter-interest-${v.key}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] border transition-colors ${
                      checked
                        ? "bg-[#10B981] border-[#10B981] text-[#0A2540]"
                        : "bg-transparent border-white/20 text-slate-500 hover:border-white/40 hover:text-slate-300"
                    }`}
                  >
                    {checked && <span aria-hidden>✓</span>}
                    {v.label}
                  </button>
                );
              })}
              </div>
            </div>

            <form
              onSubmit={submit}
              data-testid="newsletter-form"
              aria-label="Subscribe to the CarSavr editorial brief"
              className="flex flex-col sm:flex-row sm:items-stretch max-w-md border border-white/15 bg-white/5 focus-within:border-[#10B981] transition-colors"
            >
              {/* Iter-196.WW (L-6) — Honeypot anti-spam field. Hidden
                  off-screen from real users; spider bots fill it
                  anyway. Backend dead-letters any signup with a
                  populated `website` field into newsletter_spam
                  without firing the DOI email pipeline. */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                data-testid="newsletter-honeypot"
                className="absolute left-[-9999px] top-[-9999px] h-0 w-0 opacity-0 pointer-events-none"
              />
              {/* Iter-410 (footer mobile sweep) — Icon + input grouped in
                  their own row so the form stacks cleanly on <sm screens:
                  full-width input on top, full-width CTA below. Previously
                  the single flex row forced the whitespace-nowrap button
                  past the form's right border on 390px viewports. */}
              <div className="flex items-center flex-1 min-w-0">
                <div className="pl-4 grid place-items-center shrink-0">
                  <Mail className="w-4 h-4 text-slate-500" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="newsletter-email-input"
                  required
                  className="flex-1 min-w-0 bg-transparent text-white placeholder:text-slate-500 px-3 h-11 outline-none text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={loading || Object.values(interests).every((v) => !v)}
                data-testid="newsletter-submit-button"
                className="bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold h-11 px-5 transition-all disabled:opacity-60 inline-flex items-center justify-center gap-1.5 text-sm whitespace-nowrap shrink-0 border-t border-white/10 sm:border-t-0"
              >
                {loading ? "…" : "Get Free Tips"}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>

          {/* Iter-278.G — Footer Navigation Cleanup & Trust Architecture
              (founder audit 2026-06-29). Reduced from 26 main-column
              links to 15 to reinforce the four primary verticals:
              insurance, loans, refinance, warranty. Secondary surfaces
              (Editorial Team, Corrections, How We Make Money, Rate
              Alerts, Gap Insurance, First-Time Buyer, Lease vs Finance,
              Best-Of Roundups, etc.) are now reached via in-page contextual
              links rather than the global footer.
              See /app/FOOTER_AUDIT.md for the full audit + rationale. */}
          <div className="lg:col-span-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            <FooterCol
              heading="Save Money"
              links={[
                { label: "Auto Insurance", to: "/auto-insurance" },
                { label: "Auto Loans", to: "/auto-loans" },
                { label: "Auto Refinance", to: "/refinance" },
                { label: "Extended Warranty", to: "/warranty" },
              ]}
            />
            <FooterCol
              heading="Free Tools"
              links={[
                { label: "All Calculators", to: "/calculators" },
                { label: "Insurance Cost Estimator", to: "/auto-insurance-cost-estimator" },
                { label: "Auto Loan Calculator", to: "/auto-loan-calculator" },
                { label: "Refinance Savings Calculator", to: "/auto-loan-refinance-calculator" },
              ]}
            />
            <FooterCol
              heading="Learn"
              links={[
                { label: "Guides", to: "/guides" },
                { label: "Compare Companies", to: "/compare" },
                { label: "Glossary", to: "/glossary" },
              ]}
            />
            <FooterCol
              heading="Company"
              links={[
                { label: "About CarSavr", to: "/about" },
                { label: "Editorial Standards", to: "/editorial-standards" },
                { label: "Methodology", to: "/methodology" },
                { label: "Contact", to: "/contact" },
                { label: "Brand Kit", to: "/brand/" },
              ]}
            />
          </div>
        </div>

        {/* SEO mega-footer: States / Cities / Compare / Glossary */}
        <section
          data-testid="seo-mega-footer"
          className="py-10 border-b border-white/10"
          aria-label="Browse all CarSavr resources"
        >
          {/* Personalized geo card — only renders when IP resolves to a known state */}
          <GeoFooterCard />

          {/* Iter-51 → Iter-189.E Sprint 5 → Iter-190.A3 — Toggle on
              every breakpoint. Per user feedback (iter-190.A3), the
              `border-t` divider above this row was removed to keep
              the footer feeling spacious instead of busy. The
              `<details>` content still hydrates so crawlers index
              every state link. */}
          <button
            type="button"
            data-testid="seo-footer-toggle"
            onClick={() => setSeoOpen((v) => !v)}
            aria-expanded={seoOpen}
            className="w-full flex items-center justify-between text-left py-3 group"
          >
            <span>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#10B981] mb-1">
                Browse by region
              </p>
              <span className="font-display font-bold text-sm text-white tracking-tight">
                Insurance & loans across 50 states + D.C.
              </span>
            </span>
            <span className="flex items-center gap-3 shrink-0 ml-4">
              <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 group-hover:text-white transition-colors">
                {seoOpen ? "Hide" : "Show"} all
              </span>
              <ChevronDown
                className={`w-4 h-4 text-slate-500 group-hover:text-white transition-all ${seoOpen ? "rotate-180" : ""}`}
              />
            </span>
          </button>

          <div
            data-testid="seo-footer-region-content"
            className={`${seoOpen ? "block" : "hidden"}`}
          >
            {/* Auto Insurance — by State. Iter-51: defaultOpen=false now
                (was true). State list is 51 items — leaving it open by default
                created visual noise above the bottom row. Crawlers still
                index every link because <details> keeps content in the DOM. */}
            <FooterCollapsible
              testid="footer-collapsible-insurance-states"
              heading="Auto Insurance by State"
              viewAll={{ to: "/auto-insurance/states", label: "View all →", testid: "footer-all-states-link" }}
            >
              <ul
                data-testid="footer-states-list"
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1 md:gap-y-2 [&_a]:inline-block [&_a]:py-2.5 md:[&_a]:py-0"
              >
                {SORTED_STATES.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/insurance/${s.slug}`}
                      data-testid={`footer-state-${s.slug}`}
                      className="text-xs text-slate-500 hover:text-white transition-colors"
                    >
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </FooterCollapsible>

            <FooterCollapsible
              testid="footer-collapsible-insurance-cities"
              heading="Auto Insurance — Popular Cities"
              viewAll={{ to: "/auto-insurance/cities", label: "View all →" }}
            >
              <ul
                data-testid="footer-cities-list"
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-1 md:gap-y-2 [&_a]:inline-block [&_a]:py-1.5 md:[&_a]:py-0"
              >
                {POPULAR_CITIES.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/insurance/${c.state}/${c.slug}`}
                      data-testid={`footer-city-${c.slug}`}
                      className="text-xs text-slate-500 hover:text-white transition-colors"
                    >
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </FooterCollapsible>

            <FooterCollapsible
              testid="footer-collapsible-loans-states"
              heading="Auto Loans by State"
              viewAll={{ to: "/auto-loans/states", label: "View all →" }}
            >
              <ul
                data-testid="footer-loan-states-list"
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1 md:gap-y-2 [&_a]:inline-block [&_a]:py-2.5 md:[&_a]:py-0"
              >
                {SORTED_STATES.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/auto-loans/${s.slug}`}
                      data-testid={`footer-loan-state-${s.slug}`}
                      className="text-xs text-slate-500 hover:text-white transition-colors"
                    >
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </FooterCollapsible>

            <FooterCollapsible
              testid="footer-collapsible-loans-cities"
              heading="Auto Loans — Popular Cities"
              viewAll={{ to: "/auto-loans/cities", label: "View all →" }}
            >
              <ul
                data-testid="footer-loan-cities-list"
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-1 md:gap-y-2 [&_a]:inline-block [&_a]:py-2.5 md:[&_a]:py-0"
              >
                {/* Homepage/Footer audit (June 2026) — Fixed misleading
                    anchors: city labels previously linked to the parent
                    STATE page (`/auto-loans/{state}`). Auto-loan city
                    pages exist at `/auto-loans/{state}/{city}`, so the
                    anchor text now matches the destination — restoring
                    footer link equity to 15 city pages. */}
                {POPULAR_CITIES.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/auto-loans/${c.state}/${c.slug}`}
                      data-testid={`footer-loan-city-${c.slug}`}
                      className="text-xs text-slate-500 hover:text-white transition-colors"
                    >
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </FooterCollapsible>

            {/* Homepage/Footer audit (June 2026) — Refinance + Warranty
                by-state sections. /refinance/{state} (51 pages) and
                /warranty/{state} (51 pages) existed but had NO footer
                crawl path, leaving two of the four verticals
                geographically shallow in Google's eyes. Same collapsed
                <details> pattern as the sections above: links stay in
                the DOM for crawlers, zero added visual noise. viewAll
                points at the vertical hubs (no dedicated /states hub
                exists for these two verticals). */}
            <FooterCollapsible
              testid="footer-collapsible-refinance-states"
              heading="Auto Refinance by State"
              viewAll={{ to: "/refinance", label: "Refinance hub →", testid: "footer-refinance-hub-link" }}
            >
              <ul
                data-testid="footer-refinance-states-list"
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1 md:gap-y-2 [&_a]:inline-block [&_a]:py-2.5 md:[&_a]:py-0"
              >
                {SORTED_STATES.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/refinance/${s.slug}`}
                      data-testid={`footer-refinance-state-${s.slug}`}
                      className="text-xs text-slate-500 hover:text-white transition-colors"
                    >
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </FooterCollapsible>

            <FooterCollapsible
              testid="footer-collapsible-warranty-states"
              heading="Extended Warranty by State"
              viewAll={{ to: "/warranty", label: "Warranty hub →", testid: "footer-warranty-hub-link" }}
            >
              <ul
                data-testid="footer-warranty-states-list"
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1 md:gap-y-2 [&_a]:inline-block [&_a]:py-2.5 md:[&_a]:py-0"
              >
                {SORTED_STATES.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/warranty/${s.slug}`}
                      data-testid={`footer-warranty-state-${s.slug}`}
                      className="text-xs text-slate-500 hover:text-white transition-colors"
                    >
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </FooterCollapsible>

            {/* Compare & Learn — wraps the Compare Options + Glossary Picks
                pair under a single parent collapsible. Iter-189.E Sprint 5
                — closed by default on every breakpoint (user request).
                Lives OUTSIDE the Browse-by-Region content block so it
                appears even when the regional section is collapsed. */}
          </div>

          <button
            type="button"
            data-testid="seo-footer-compare-toggle"
            onClick={() => setCompareOpen((v) => !v)}
            aria-expanded={compareOpen}
            className="w-full flex items-center justify-between text-left py-3 border-t border-white/10 group"
          >
            <span>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#10B981] mb-1">
                Compare &amp; Learn
              </p>
              <span className="font-display font-bold text-sm text-white tracking-tight">
                Side-by-side comparisons &amp; the glossary
              </span>
            </span>
            <span className="flex items-center gap-3 shrink-0 ml-4">
              <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 group-hover:text-white transition-colors">
                {compareOpen ? "Hide" : "Show"} all
              </span>
              <ChevronDown
                className={`w-4 h-4 text-slate-500 group-hover:text-white transition-all ${compareOpen ? "rotate-180" : ""}`}
              />
            </span>
          </button>

          <div
            data-testid="seo-footer-compare-content"
            className={`${compareOpen ? "block" : "hidden"}`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 pt-2 pb-6">
              <FooterMegaCol
                heading="Compare Options"
                viewAllTo="/compare"
                testid="footer-compare-col"
              >
                {compareList.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/${c.slug}`}
                      data-testid={`footer-compare-${c.slug}`}
                      className="text-sm text-slate-300 hover:text-white transition-colors"
                    >
                      {c.title}
                    </Link>
                  </li>
                ))}
              </FooterMegaCol>

              <FooterMegaCol
                heading="Glossary Picks"
                viewAllTo="/glossary"
                testid="footer-glossary-col"
              >
                {glossaryList.map((g) => (
                  <li key={g.slug}>
                    <Link
                      to={`/glossary/${g.slug}`}
                      data-testid={`footer-glossary-${g.slug}`}
                      className="text-sm text-slate-300 hover:text-white transition-colors"
                    >
                      {g.term}
                    </Link>
                  </li>
                ))}
              </FooterMegaCol>
            </div>
          </div>
        </section>

        {/* Iter-51: Restructured bottom row — premium-publisher layout.
            Row 1: legal nav (Privacy · Terms · Disclosure · Editorial · Accessibility · Contact)
            Row 2: brand/copyright/entity attribution + social
            Cleaner hierarchy than the previous single-line dot-separated layout. */}
        <div className="pt-8">
          <nav
            data-testid="footer-legal-nav"
            aria-label="Legal and policy links"
            className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500 pb-6 border-b border-white/10 [&_a]:py-2 md:[&_a]:py-0"
          >
            <Link to="/about" data-testid="footer-legal-about" className="hover:text-white transition-colors">About</Link>
            <Link to="/contact" data-testid="footer-legal-contact" className="hover:text-white transition-colors">Contact</Link>
            {/* Iter-275.AR (Phase 1 #17 of Production Readiness Audit) —
                Visible customer-service email on every page. Required by
                Insurify / Bankrate / Policygenius affiliate-program
                compliance reviews + matches the publisher norm for
                YMYL finance sites. The /contact form lives one click
                away, but reviewers expect a direct mailto: too.
                hello@ is the canonical customer-facing inbox; partnerships@
                exists separately for affiliate-program outreach. */}
            <a
              href="mailto:hello@carsavr.com"
              data-testid="footer-legal-support-email"
              className="hover:text-white transition-colors"
            >
              hello@carsavr.com
            </a>
            <Link to="/privacy" data-testid="footer-legal-privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/do-not-sell" data-testid="footer-legal-do-not-sell" className="hover:text-white transition-colors">Do Not Sell My Personal Information</Link>
            <button
              type="button"
              onClick={() => resetConsent()}
              data-testid="footer-legal-cookie-settings"
              className="hover:text-white transition-colors text-left"
            >
              Cookie Settings
            </button>
            <Link to="/terms" data-testid="footer-legal-terms" className="hover:text-white transition-colors">Terms of Use</Link>
            <Link to="/disclosure" data-testid="footer-legal-disclosure" className="hover:text-white transition-colors">Affiliate Disclosure</Link>
            {/* Iter-278.G — Editorial Standards / Methodology / How We
                Make Money were duplicated here AND in the main Company
                column. Deduped per founder audit: legal bar now houses
                only true legal/compliance pages. Editorial Standards +
                Methodology continue to surface in the Company column. */}
            <Link to="/accessibility" data-testid="footer-legal-accessibility" className="hover:text-white transition-colors">Accessibility</Link>
            {/* Iter-189.G(b) — Brand-kit downloads. Journalists +
                press partners can grab the canonical SVG wordmarks
                + monogram without contacting us. `download` attr
                forces a save-as instead of opening in a new tab. */}
            <Link
              to="/brand/"
              data-testid="footer-legal-brand-kit"
              className="hover:text-white transition-colors"
            >
              Brand kit
            </Link>
          </nav>

          <div className="pt-6 flex flex-col-reverse md:flex-row md:items-end md:justify-between gap-6">
            <div className="text-xs text-slate-500 space-y-2 leading-relaxed">
              <p>
                © {new Date().getFullYear()} CarSavr.com — Compare. Save. Drive.
              </p>
              <p className="flex items-center gap-1.5 text-slate-500">
                <Building2 className="w-3 h-3" />
                Operated by{" "}
                <span className="text-slate-500 font-semibold">
                  Ecke National Holdings, LLC
                </span>
              </p>
            </div>

            <div
              className="flex items-center gap-2.5 flex-wrap"
              data-testid="footer-social-icons"
            >
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  aria-label={s.label}
                  data-testid={`footer-social-${s.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 border border-white/15 hover:border-[#10B981] hover:text-[#10B981] text-slate-500 grid place-items-center transition-colors"
                >
                  <SocialIcon>{s.path}</SocialIcon>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Affiliate disclosure — FTC-grade universal disclosure strip */}
        <div
          data-testid="footer-affiliate-disclosure"
          className="mt-8 border border-white/10 bg-slate-950/50 p-4 lg:p-5 flex items-start gap-3"
        >
          <Info className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
          <p className="text-[11px] text-slate-500 leading-relaxed">
            <span className="font-bold text-slate-200 uppercase tracking-[0.15em] text-[10px] mr-1">
              Affiliate Disclosure ·
            </span>
            CarSavr.com, operated by Ecke National Holdings, LLC, may earn a
            commission when readers click partner offers, request a quote, or open
            an account with a featured provider. This compensation funds our free
            tools but never determines which offers we cover, the order in which
            they appear, or our editorial assessment.{" "}
            <Link
              to="/disclosure"
              data-testid="footer-affiliate-disclosure-about-link"
              className="font-semibold text-slate-200 hover:text-[#10B981] underline-offset-2 hover:underline transition-colors"
            >
              Full disclosure
            </Link>
            {" · "}
            <Link
              to="/editorial-standards"
              data-testid="footer-affiliate-disclosure-terms-link"
              className="font-semibold text-slate-200 hover:text-[#10B981] underline-offset-2 hover:underline transition-colors"
            >
              Editorial policy
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ heading, links }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#10B981] mb-4">
        {heading}
      </p>
      <ul className="space-y-1 md:space-y-2.5">
        {links.map((l) => (
          <li key={l.label}>
            <Link
              to={l.to}
              className="block py-1.5 md:py-0 text-sm text-slate-300 hover:text-white transition-colors"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterMegaCol({ heading, viewAllTo, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#10B981]">
          {heading}
        </p>
        <Link
          to={viewAllTo}
          className="text-[11px] text-slate-500 hover:text-white transition-colors py-1.5 md:py-0"
        >
          View all →
        </Link>
      </div>
      <ul className="space-y-1 md:space-y-2.5 [&_a]:block [&_a]:py-1.5 md:[&_a]:py-0">{children}</ul>
    </div>
  );
}

// Native <details>/<summary> collapsible — accessible by default, content stays
// in the DOM so search-engine crawlers index every link regardless of UI state.
// `defaultOpen` matters only for the FIRST render; users' clicks override it.
function FooterCollapsible({ heading, testid, viewAll, defaultOpen = false, children }) {
  return (
    <details
      data-testid={testid}
      open={defaultOpen}
      className="group mb-3 border-b border-white/10 last:border-b-0"
    >
      <summary
        className="flex items-center justify-between cursor-pointer list-none py-4"
        aria-label={`Toggle ${heading}`}
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#10B981]">
          {heading}
        </span>
        <span className="flex items-center gap-3">
          {viewAll && (
            <Link
              to={viewAll.to}
              data-testid={viewAll.testid}
              onClick={(e) => e.stopPropagation()}
              className="text-[11px] text-slate-500 hover:text-white transition-colors"
            >
              {viewAll.label || "View all →"}
            </Link>
          )}
          <ChevronDown
            aria-hidden
            className="w-4 h-4 text-slate-500 transition-transform group-open:rotate-180 shrink-0"
          />
        </span>
      </summary>
      <div className="pb-6 pt-1">{children}</div>
    </details>
  );
}
